import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { FirebaseError } from 'firebase/app';

import { AttractionsDataResponse, AttractionsFirestoreResponse } from '../../types';
import { ATTRACTIONS_PER_PAGE } from '../../variables';
import { fetchDocumentsByIds } from '../utils';

const COLLECTION_PATH = 'attractions';

export const useGetAttractionsByIds = (
  associatedAttractions: string[],
  page: number,
  options?: UseQueryOptions<AttractionsDataResponse, FirebaseError, AttractionsDataResponse>,
): UseQueryResult<AttractionsDataResponse, FirebaseError> => useQuery<AttractionsDataResponse,
    FirebaseError, AttractionsDataResponse>({
      queryKey: [COLLECTION_PATH, associatedAttractions, page] as const,
      queryFn: async (): Promise<AttractionsDataResponse> => {
        if (!associatedAttractions.length) return { data: [], totalPages: 0 };

        // Fetch all documents by IDs first
        const fetchedData = await fetchDocumentsByIds<AttractionsFirestoreResponse>(
          COLLECTION_PATH,
          associatedAttractions,
        );

        // Implement manual pagination
        const totalDocuments = fetchedData.length;
        const totalPages = Math.ceil(totalDocuments / ATTRACTIONS_PER_PAGE);

        if (page < 1 || page > totalPages) {
          throw new FirebaseError('invalid-argument', `Invalid page number: ${page}`);
        }

        const startIndex = (page - 1) * ATTRACTIONS_PER_PAGE;
        const paginatedData = fetchedData.slice(startIndex, startIndex + ATTRACTIONS_PER_PAGE);

        const mappedData = paginatedData.map((item) => ({
          id: item.id,
          title: item.title,
          address: item.address,
          city: item.city,
          createdAt: item.createdAt,
          thumbnail: item.thumbnail,
          type: item.type,
          translationStatus: item.translationStatus || undefined,
        }));

        return { data: mappedData, totalPages };
      },
      retry: 0,
      ...options,
    });
